import { useState, useEffect, useCallback, useRef, useMemo } from 'react';

import { supabase } from '../lib/supabase';
import { getSessionConfig } from '../shared/config/sessionConfig';
import type { SessionConfig } from '../shared/config/sessionConfig';
import { Service, Action, logInfo, logError } from '../shared/utils/LogUtility';

export interface SessionTimeoutConfig extends SessionConfig {}

/** Throttle activity-driven timer resets (avoid mousemove spam). */
const ACTIVITY_RESET_THROTTLE_MS = 30_000;

export const useSessionTimeout = (configOverride?: SessionTimeoutConfig) => {
  const config = useMemo(
    () => configOverride ?? getSessionConfig(),
    [configOverride],
  );

  const [isWarningVisible, setIsWarningVisible] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [isActive, setIsActive] = useState(true);

  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const warningTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const countdownRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const lastActivityResetRef = useRef<number>(0);
  const isTimingOutRef = useRef(false);

  const activityEvents = useMemo(
    () => ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click', 'keydown'],
    [],
  );

  const clearAllTimers = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (warningTimeoutRef.current) {
      clearTimeout(warningTimeoutRef.current);
      warningTimeoutRef.current = null;
    }
    if (countdownRef.current) {
      clearInterval(countdownRef.current);
      countdownRef.current = null;
    }
  }, []);

  const handleTimeout = useCallback(async () => {
    if (isTimingOutRef.current) return;
    isTimingOutRef.current = true;
    clearAllTimers();

    try {
      const paymentInProgress = localStorage.getItem('payment_in_progress');
      if (paymentInProgress) {
        console.log('Payment in progress - preventing auto-logout');
        isTimingOutRef.current = false;
        return;
      }

      await supabase.auth.signOut();

      sessionStorage.clear();
      localStorage.removeItem('feedback_dont_ask_again');
      localStorage.removeItem('last_feedback_date');
      window.location.href = '/';
    } catch (error) {
      logError(Service.SYSTEM, Action.FAIL, 'Error during session timeout:', error as Record<string, unknown>);
      window.location.href = '/';
    }
  }, [clearAllTimers]);

  const resetTimers = useCallback(() => {
    if (!config.enabled) return;

    const paymentInProgress = localStorage.getItem('payment_in_progress');
    if (paymentInProgress) {
      console.log('Payment in progress - skipping session timeout');
      return;
    }

    isTimingOutRef.current = false;
    setIsActive(true);
    setIsWarningVisible(false);
    clearAllTimers();

    const warningDelay = (config.timeoutMinutes - config.warningMinutes) * 60 * 1000;
    const mainDelay = config.timeoutMinutes * 60 * 1000;

    warningTimeoutRef.current = setTimeout(() => {
      logInfo(Service.SYSTEM, Action.PROCESS, 'Session warning triggered');
      setIsWarningVisible(true);
      setTimeRemaining(config.warningMinutes * 60);

      countdownRef.current = setInterval(() => {
        setTimeRemaining((prev) => {
          if (prev <= 1) {
            logInfo(Service.SYSTEM, Action.LOGIN, 'Warning countdown expired - signing out');
            if (countdownRef.current) {
              clearInterval(countdownRef.current);
              countdownRef.current = null;
            }
            void handleTimeout();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }, warningDelay);

    timeoutRef.current = setTimeout(() => {
      logInfo(Service.SYSTEM, Action.PROCESS, 'Session timeout reached - signing out');
      void handleTimeout();
    }, mainDelay);
  }, [clearAllTimers, config, handleTimeout]);

  const extendSession = useCallback(() => {
    lastActivityResetRef.current = Date.now();
    setIsWarningVisible(false);
    setTimeRemaining(0);
    resetTimers();
  }, [resetTimers]);

  const signOutNow = useCallback(async () => {
    try {
      await supabase.auth.signOut();
      sessionStorage.clear();
      localStorage.removeItem('feedback_dont_ask_again');
      localStorage.removeItem('last_feedback_date');
      window.location.href = '/';
    } catch (error) {
      logError(Service.SYSTEM, Action.LOGIN, 'Error signing out:', error as Record<string, unknown>);
      window.location.href = '/';
    }
  }, []);

  useEffect(() => {
    if (!config.enabled) return;

    const handleActivity = () => {
      const now = Date.now();
      if (now - lastActivityResetRef.current < ACTIVITY_RESET_THROTTLE_MS) return;
      lastActivityResetRef.current = now;
      resetTimers();
    };

    activityEvents.forEach((event) => {
      document.addEventListener(event, handleActivity, true);
    });

    lastActivityResetRef.current = Date.now();
    resetTimers();

    return () => {
      activityEvents.forEach((event) => {
        document.removeEventListener(event, handleActivity, true);
      });
      clearAllTimers();
    };
  }, [activityEvents, clearAllTimers, config.enabled, config.timeoutMinutes, config.warningMinutes, resetTimers]);

  const formatTimeRemaining = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  return {
    isWarningVisible,
    timeRemaining: formatTimeRemaining(timeRemaining),
    isActive,
    extendSession,
    signOutNow,
    config,
  };
};
